/**
 * Administrator «Об'єкти» (iteration 2): objects of all clients, a card each —
 * name; client (name, phone, email); model, SN, region; connection; history
 * download; internal arbitrage; (D3/D4) access of Phage.Energy — data and
 * control go through it — and the date it was checked; VPP application;
 * (owner, 24.10.2026) the installer's battery currents and the plan's limits
 * by them; charge. A card
 * opens the same object screen as the client's.
 */
import { Box } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import type { AdminSiteRow, AdminSitesResponse } from 'shared';

import { accessStatusKey } from '../../components/PhageAccess.js';
import { useLocale } from '../../i18n/locale-context.js';
import { api } from '../../lib/api-client.js';
import {
  countLabel,
  fill,
  formatDate,
  formatDateTime,
  formatNumber,
  formatPhone,
  formatTime,
  modelLabel,
  regionLabel,
} from './admin-format.js';
import { ADMIN_TOKENS, AdminPage, Chip, NARROW, QueryState, StateCard, useIsAdmin } from './admin-ui.js';

/** Live poll runs every 5 minutes; three missed polls — «Немає зв'язку з …». */
const OFFLINE_AFTER_MS = 15 * 60_000;
/** Depth of the initial history download (`HISTORY_DEPTH_DAYS`, api). */
const HISTORY_DAYS = 90;
const T = ADMIN_TOKENS;
/** A long name or email breaks inside a word rather than widen the page on a phone. */
const WRAP = { overflowWrap: 'anywhere' } as const;

export function OperatorSitesPage() {
  const { t, locale } = useLocale();
  const isAdmin = useIsAdmin();
  const query = useQuery({
    queryKey: ['admin', 'sites'],
    queryFn: () => api.get<AdminSitesResponse>('/api/admin/sites'),
    enabled: isAdmin,
    refetchInterval: 60_000,
  });
  const sites = query.data?.sites;
  const clients = new Set(sites?.map((s) => s.client.id)).size;
  // «На зв'язку» goes stale by the clock too: a refetch with the same answer re-renders nothing.
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 60_000);
    return () => clearInterval(id);
  }, []);

  return (
    <AdminPage
      title={t('operator.sites.title')}
      sub={
        sites?.length
          ? fill(t('operator.sites.sub'), {
              objects: countLabel(t, locale, 'operator.count.objects', sites.length),
              clients: countLabel(t, locale, 'operator.count.clients', clients),
            })
          : null
      }
    >
      {!sites ? (
        <QueryState error={query.error} onRetry={() => query.refetch()} />
      ) : sites.length === 0 ? (
        <StateCard title={t('operator.sites.empty.title')} text={t('operator.sites.empty.text')} />
      ) : (
        <Box sx={{ display: 'grid', gap: '12px' }}>
          {sites.map((site) => (
            <SiteCard key={site.site_id} site={site} now={now} />
          ))}
        </Box>
      )}
    </AdminPage>
  );
}

function SiteCard({ site, now }: { site: AdminSiteRow; now: number }) {
  const { t, locale } = useLocale();
  // D4: without Phage.Energy's access no data comes and arbitrage stops.
  const noAccess = site.phage_access.status === 'pending' || site.phage_access.status === 'closed';
  const who = [site.client.name, site.client.phone ? formatPhone(site.client.phone) : null, site.client.email]
    .filter(Boolean)
    .join(' · ');
  const meta = [
    site.model ? modelLabel(site.model) : null,
    fill(t('operator.sn'), { sn: site.device_sn }),
    site.region ? regionLabel(site.region, locale) : null,
  ]
    .filter(Boolean)
    .join(' · ');

  return (
    <Box
      component={Link}
      to={`/app/sites/${site.site_id}`}
      sx={{
        display: 'grid',
        gridTemplateColumns: 'minmax(0, 1fr) auto',
        gap: '8px 16px',
        alignItems: 'center',
        textDecoration: 'none',
        color: 'inherit',
        bgcolor: T.bg,
        border: `1px solid ${T.line}`,
        borderRadius: '12px',
        boxShadow: T.shadow,
        px: '20px',
        py: '18px',
        '&:hover': { borderColor: T.lineStrong },
        '&:focus-visible': { outline: `2px solid ${T.sky}`, outlineOffset: '2px' },
      }}
    >
      <Box sx={{ minWidth: 0 }}>
        <Box component="h2" sx={{ m: 0, fontSize: 17, fontWeight: 600, textWrap: 'balance', ...WRAP }}>
          {site.site_name}
        </Box>
        <Box sx={{ fontSize: 13.5, mt: '3px', fontVariantNumeric: 'tabular-nums', ...WRAP }}>{who}</Box>
        <Box sx={{ color: T.slate, fontSize: 13, mt: '3px', fontVariantNumeric: 'tabular-nums', ...WRAP }}>
          {meta}
        </Box>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '8px', mt: '10px' }}>
          <ConnectionChip site={site} now={now} />
          <HistoryChip site={site} />
          <Chip label={t('operator.sites.arbitrage')}>
            {!site.internal_arbitrage_enabled
              ? t('operator.sites.arbitrage.off')
              : noAccess
                ? t('operator.sites.arbitrage.stopped')
                : t('operator.sites.arbitrage.on')}
          </Chip>
          <Chip
            label={t('operator.sites.phage')}
            {...(site.phage_access.status === 'open' ? {} : { tone: 'warn' })}
          >
            {[
              t(accessStatusKey(site.phage_access.status)),
              site.phage_access.checked_at ? formatDate(site.phage_access.checked_at) : null,
            ]
              .filter(Boolean)
              .join(' · ')}
          </Chip>
          {site.application_created_at ? (
            <Chip label={t('operator.sites.application')}>{formatDate(site.application_created_at)}</Chip>
          ) : null}
          {site.program_changed ? (
            <Chip label={t('operator.sites.program_changed')} tone="warn">
              {fill(t('operator.sites.program_changed.value'), {
                at: formatDateTime(site.program_changed.at),
              })}
            </Chip>
          ) : null}
        </Box>
        <CurrentsChips site={site} />
      </Box>
      <Box sx={{ textAlign: 'right' }}>
        <Box component="span" sx={{ color: T.slate, fontSize: 13 }}>
          {t('operator.sites.soc')}
        </Box>
        {noAccess || site.soc_pct === null ? (
          <Box component="b" sx={{ display: 'block', fontSize: 15, fontWeight: 600 }}>
            {noAccess ? t('operator.sites.soc.no_connection') : t('operator.sites.soc.no_data')}
          </Box>
        ) : (
          <Box
            component="b"
            sx={{
              display: 'block',
              fontSize: 22,
              fontWeight: 600,
              fontVariantNumeric: 'tabular-nums',
              whiteSpace: 'nowrap',
              [NARROW]: { fontSize: 20 },
            }}
          >
            {formatNumber(site.soc_pct, locale, 1)} %
          </Box>
        )}
      </Box>
    </Box>
  );
}

/**
 * «DeyeCloud не відповідає з 14:40», «На зв'язку» or «Немає зв'язку з 11:05»;
 * nothing before the first sample or without Phage.Energy's access (its chip
 * says so).
 */
function ConnectionChip({ site, now }: { site: AdminSiteRow; now: number }) {
  const { t } = useLocale();
  const today = formatDate(new Date(now).toISOString());
  const since = (iso: string) => (formatDate(iso) === today ? formatTime(iso) : formatDateTime(iso));
  if (site.phage_access.status === 'pending' || site.phage_access.status === 'closed') return null;
  if (site.vendor_down_since) {
    return <Chip>{fill(t('operator.sites.vendor_down'), { time: since(site.vendor_down_since) })}</Chip>;
  }
  if (!site.last_seen_at) return null;
  if (now - Date.parse(site.last_seen_at) <= OFFLINE_AFTER_MS) {
    return <Chip dot>{t('operator.sites.online')}</Chip>;
  }
  return (
    <Chip tone="warn">{fill(t('operator.sites.offline_since'), { time: since(site.last_seen_at) })}</Chip>
  );
}

/** Only while the history is not complete: «завантажуємо: 36 з 90 днів» or the error. */
function HistoryChip({ site }: { site: AdminSiteRow }) {
  const { t } = useLocale();
  const analysis = site.analysis;
  if (!analysis || analysis.status === 'done') return null;
  if (analysis.status === 'failed') {
    return (
      <Chip label={t('operator.sites.history')} tone="warn">
        {t('operator.sites.history.failed')}
      </Chip>
    );
  }
  const done =
    analysis.windows_total > 0
      ? Math.round((HISTORY_DAYS * analysis.windows_done) / analysis.windows_total)
      : 0;
  return (
    <Chip label={t('operator.sites.history')}>
      {fill(t('operator.sites.history.loading'), { done, total: HISTORY_DAYS })}
    </Chip>
  );
}

/**
 * Owner, 24.10.2026: the battery currents — Max A Charge / Discharge never
 * written by Phage, the grid charge current set for the program's grid charge
 * (the installer's beside it) — as the plan takes them, where from, the
 * battery voltage — and the plan's limits by them: «Струми інсталятора: заряд 5 А · розряд 5 А · від
 * мережі 25 А · батарея 51,2 В · прочитано 23.10.2026», «План у цих межах:
 * розряд до 0,2 кВт · заряд від мережі до 0,2 кВт · інвертор 10 кВт».
 */
function CurrentsChips({ site }: { site: AdminSiteRow }) {
  const { t, locale } = useLocale();
  const c = site.currents;
  const amps = (a: number | null) =>
    a === null ? '—' : fill(t('operator.sites.currents.amps'), { a: formatNumber(a, locale) });
  const kw = (v: number) => formatNumber(v, locale, Number.isInteger(v) ? 0 : 1);
  const zero = c.charge_a === 0 || c.discharge_a === 0;
  const currents = [
    c.source
      ? fill(t('operator.sites.currents.value'), {
          charge: amps(c.charge_a),
          discharge: amps(c.discharge_a),
          // The program's own grid charge current: the installer's beside it.
          grid:
            c.grid_charge_a_original !== null && c.grid_charge_a_original !== c.grid_charge_a
              ? fill(t('operator.sites.currents.grid_program'), {
                  now: amps(c.grid_charge_a),
                  original: amps(c.grid_charge_a_original),
                })
              : amps(c.grid_charge_a),
        })
      : t('operator.sites.currents.none'),
    c.battery_v !== null
      ? fill(t('operator.sites.currents.volts'), { v: formatNumber(c.battery_v, locale, 1) })
      : null,
    c.grid_charge_on === false ? t('operator.sites.currents.grid_off') : null,
    c.by_phage ? t('operator.sites.currents.by_phage') : null,
    c.source && c.at
      ? fill(t(c.source === 'read' ? 'operator.sites.currents.read' : 'operator.sites.currents.original'), {
          date: formatDate(c.at),
        })
      : null,
  ];
  const grid = c.grid_charge_kw ?? c.power_kw;
  const limits = [
    grid > 0
      ? fill(t('operator.sites.limits.value'), { power: kw(c.power_kw), grid: kw(grid) })
      : fill(t('operator.sites.limits.no_grid'), { power: kw(c.power_kw) }),
    fill(t('operator.sites.limits.rated'), { rated: kw(c.rated_kw) }),
  ];
  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '8px', mt: '8px' }} data-testid="admin-currents">
      <Chip label={t('operator.sites.currents')} {...(c.by_phage || zero ? { tone: 'warn' } : {})}>
        {currents.filter(Boolean).join(' · ')}
      </Chip>
      <Chip label={t('operator.sites.limits')}>{limits.join(' · ')}</Chip>
    </Box>
  );
}
